import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HostWithStats } from '../services/hostService.ts';

type AuditFilterKey = '' | 'missingGeo' | 'missingCover' | 'svgCover' | 'missingDesc' | 'missingUrl';

interface HostsTableProps {
  hosts: HostWithStats[];
  loading: boolean;
  postCountsLoading?: boolean;
  onSelectHost: (host: HostWithStats) => void;
  selectedHostId?: string;
  externalAuditFilter?: AuditFilterKey;
  onClearAuditFilter?: () => void;
}

type SortKey = 'title' | 'host_type' | 'linkedPostCount' | 'updatedAt' | 'coverStatus' | 'hasGeo' | 'hasDescription';

const STATUS_ICON: Record<string, string> = {
  ok: '\u2705',
  svg: '\u26A0\uFE0F',
  missing: '\u274C',
};

export const HostsTable: React.FC<HostsTableProps> = ({
  hosts, loading, postCountsLoading, onSelectHost, selectedHostId,
  externalAuditFilter, onClearAuditFilter,
}) => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortAsc, setSortAsc] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [localAuditFilter, setLocalAuditFilter] = useState<AuditFilterKey>('');

  const auditFilter = externalAuditFilter || localAuditFilter;

  useEffect(() => {
    if (externalAuditFilter) {
      setLocalAuditFilter(externalAuditFilter);
    }
  }, [externalAuditFilter]);

  const handleAuditFilterChange = (val: AuditFilterKey) => {
    setLocalAuditFilter(val);
    if (!val && onClearAuditFilter) onClearAuditFilter();
  };

  const uniqueTypes = useMemo(
    () => Array.from(new Set(hosts.map((h) => h.host_type || 'Unknown'))).sort(),
    [hosts],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return hosts.filter((h) => {
      if (q) {
        const hay = [h.title, h.id, h.officialUrl, ...(h.aliases || [])].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (typeFilter && (h.host_type || 'Unknown') !== typeFilter) return false;
      if (auditFilter === 'missingGeo' && h.hasGeo) return false;
      if (auditFilter === 'missingCover' && h.coverStatus !== 'missing') return false;
      if (auditFilter === 'svgCover' && h.coverStatus !== 'svg') return false;
      if (auditFilter === 'missingDesc' && h.hasDescription) return false;
      if (auditFilter === 'missingUrl' && h.officialUrl) return false;
      return true;
    });
  }, [hosts, search, typeFilter, auditFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'title':
          cmp = (a.title || '').localeCompare(b.title || '');
          break;
        case 'host_type':
          cmp = (a.host_type || '').localeCompare(b.host_type || '');
          break;
        case 'linkedPostCount':
          cmp = a.linkedPostCount - b.linkedPostCount;
          break;
        case 'updatedAt':
          cmp = (a.updatedAt || '').localeCompare(b.updatedAt || '');
          break;
        case 'coverStatus': {
          const order: Record<string, number> = { ok: 0, svg: 1, missing: 2 };
          cmp = (order[a.coverStatus] ?? 3) - (order[b.coverStatus] ?? 3);
          break;
        }
        case 'hasGeo':
          cmp = (a.hasGeo ? 0 : 1) - (b.hasGeo ? 0 : 1);
          break;
        case 'hasDescription':
          cmp = (a.hasDescription ? 0 : 1) - (b.hasDescription ? 0 : 1);
          break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortAsc]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const sortArrow = (key: SortKey) => {
    if (sortKey !== key) return '';
    return sortAsc ? ' \u2191' : ' \u2193';
  };

  const safeHostname = (url: string) => {
    try { return new URL(url).hostname.replace('www.', '').slice(0, 28); }
    catch { return url.slice(0, 28); }
  };

  if (loading) {
    return <div className="hosts-table-loading">Loading hosts...</div>;
  }

  if (hosts.length === 0) {
    return (
      <div className="hosts-empty-state">
        <p className="hosts-empty-title">No hosts in Firestore</p>
        <p className="hosts-empty-body">
          The dashboard reads the <code>Hosts</code> collection. If you expected data here, confirm you are signed in as an admin,
          Firestore rules allow reads, and documents exist in that collection.
        </p>
      </div>
    );
  }

  return (
    <div className="hosts-table-section">
      {postCountsLoading && (
        <p className="hosts-postcounts-hint">Loading linked post counts (aggregation queries)…</p>
      )}
      <div className="hosts-table-controls">
        <input
          type="search"
          placeholder="Search hosts by title, ID, URL, alias..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="hosts-search-input"
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="hosts-filter-select">
          <option value="">All Types</option>
          {uniqueTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={auditFilter}
          onChange={(e) => handleAuditFilterChange(e.target.value as AuditFilterKey)}
          className="hosts-filter-select"
        >
          <option value="">All Hosts</option>
          <option value="missingGeo">Missing Geo</option>
          <option value="missingCover">Missing Cover</option>
          <option value="svgCover">SVG Cover</option>
          <option value="missingDesc">Weak Description</option>
          <option value="missingUrl">Missing URL</option>
        </select>
        <span className="hosts-result-count">
          {sorted.length} of {hosts.length} hosts
        </span>
      </div>

      <div className="hosts-table-wrapper">
        <table className="hosts-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('title')} className="sortable">Title{sortArrow('title')}</th>
              <th onClick={() => handleSort('host_type')} className="sortable">Type{sortArrow('host_type')}</th>
              <th onClick={() => handleSort('coverStatus')} className="sortable">Cover{sortArrow('coverStatus')}</th>
              <th onClick={() => handleSort('hasGeo')} className="sortable">Geo{sortArrow('hasGeo')}</th>
              <th onClick={() => handleSort('hasDescription')} className="sortable">Desc{sortArrow('hasDescription')}</th>
              <th>URL</th>
              <th title="Alternate names / place strings stored on the host for matching events to this venue (see host profile pipeline).">
                Aliases
              </th>
              <th onClick={() => handleSort('linkedPostCount')} className="sortable">Posts{sortArrow('linkedPostCount')}</th>
              <th onClick={() => handleSort('updatedAt')} className="sortable">Updated{sortArrow('updatedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={9} className="hosts-empty-row">No hosts match the current filters.</td></tr>
            ) : (
              sorted.map((host) => (
                <tr
                  key={host.id}
                  className={`hosts-row ${selectedHostId === host.id ? 'selected' : ''}`}
                  onClick={() => onSelectHost(host)}
                >
                  <td className="hosts-cell-title">
                    <span className="host-title-text">{host.title || '(untitled)'}</span>
                    <span className="host-id-text">{host.id}</span>
                  </td>
                  <td><span className={`host-type-badge type-${(host.host_type || 'unknown').toLowerCase()}`}>{host.host_type || '?'}</span></td>
                  <td className="hosts-cell-center">{STATUS_ICON[host.coverStatus]}</td>
                  <td className="hosts-cell-center">{host.hasGeo ? '\u2705' : '\u274C'}</td>
                  <td className="hosts-cell-center">{host.hasDescription ? '\u2705' : '\u274C'}</td>
                  <td className="hosts-cell-url">
                    {host.officialUrl ? (
                      <a href={host.officialUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title={host.officialUrl}>
                        {safeHostname(host.officialUrl)}
                      </a>
                    ) : (
                      <span className="hosts-missing">(none)</span>
                    )}
                  </td>
                  <td title={(host.aliases || []).length ? (host.aliases || []).join(', ') : undefined}>
                    {(host.aliases || []).length > 0 ? (host.aliases || []).length : '-'}
                  </td>
                  <td className="hosts-cell-center">
                    {postCountsLoading ? (
                      '\u2026'
                    ) : (
                      <Link
                        className="hosts-posts-link"
                        to={`/admin?hostID=${encodeURIComponent(host.id)}`}
                        title="Open Events dashboard filtered to this host"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {host.linkedPostCount}
                      </Link>
                    )}
                  </td>
                  <td className="hosts-cell-date">
                    {host.updatedAt ? new Date(host.updatedAt).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
